/**
 * Cliente HTTP del briefing — Fase 7b.4.
 *
 * Expone:
 *  - Listado de misiones (mission_reports parseados) para el dropdown
 *    CAMPAÑA/MISIÓN. Existe desde 7d.6d.
 *  - CRUD del briefing contra `/api/briefings/*`: list/get/create/update/
 *    delete + endpoints de fotos (upload/delete/file). Reemplaza la
 *    persistencia localStorage anterior.
 *
 * Convenciones:
 *  - Todas las llamadas mandan la cookie de sesión (`credentials:
 *    'include'`). El backend valida con `requireAuth`.
 *  - Las funciones devuelven `ApiResult<T>` discriminada — ok=true con
 *    data, ok=false con status/code/message para que la UI pinte
 *    feedback útil.
 */

import type { Briefing, BriefingPayload } from '@51esc/shared';

// ─── Listado de misiones (legacy de 7d.6d, sigue válido) ───────────────

export interface BriefingMission {
  id: string;
  reportNumber: string;
  title: string | null;
  uploadedAt: string;
  missionDateIso: string | null;
}

interface MissionsListResponse {
  missions: BriefingMission[];
}

export async function fetchBriefingMissions(): Promise<BriefingMission[]> {
  let res: Response;
  try {
    res = await fetch('/api/mission-reports', { credentials: 'include' });
  } catch (err) {
    console.error('[briefing] fetchMissions network err', err);
    return [];
  }
  if (!res.ok) {
    console.warn('[briefing] fetchMissions HTTP', res.status);
    return [];
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    return [];
  }
  return (body as MissionsListResponse).missions ?? [];
}

/**
 * Targets para el dropdown CAMPAÑA/MISIÓN del briefing/debriefing
 * desde Fase 8e. Reemplaza el uso anterior de `mission_reports` con
 * las entidades declarativas (`campaigns` + `missions`) que el oficial
 * crea desde la pizarra.
 */
import type { CampaignSummary, MissionSummary } from '@51esc/shared';

export interface BriefingTargets {
  campaigns: CampaignSummary[];
  missions: MissionSummary[];
}

export async function fetchBriefingTargets(): Promise<BriefingTargets> {
  let res: Response;
  try {
    res = await fetch('/api/operations', { credentials: 'include' });
  } catch (err) {
    console.error('[briefing] fetchTargets network err', err);
    return { campaigns: [], missions: [] };
  }
  if (!res.ok) {
    console.warn('[briefing] fetchTargets HTTP', res.status);
    return { campaigns: [], missions: [] };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    return { campaigns: [], missions: [] };
  }
  const b = body as { campaigns?: CampaignSummary[]; missions?: MissionSummary[] };
  return { campaigns: b.campaigns ?? [], missions: b.missions ?? [] };
}

// ─── Tipo de resultado discriminado ────────────────────────────────────

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; code: string; message: string };

interface ApiErrorBody {
  error?: string;
  code?: string;
}

/**
 * Wrapper de `fetch` con desempaquetado JSON, manejo de 204, errores
 * estandarizados y unwrap opcional. Siempre manda credentials.
 */
async function apiCall<T>(
  method: string,
  path: string,
  body?: unknown,
  unwrap?: (json: unknown) => T,
): Promise<ApiResult<T>> {
  const init: RequestInit = { method, credentials: 'include' };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { 'Content-Type': 'application/json' };
  }

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    console.error('[api] network', method, path, err);
    return {
      ok: false,
      status: 0,
      code: 'network',
      message: 'Sin conexión con el servidor.',
    };
  }

  if (res.status === 204) {
    return { ok: true, data: undefined as T };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    return {
      ok: false,
      status: res.status,
      code: 'bad_response',
      message: 'Respuesta inválida del servidor.',
    };
  }

  if (!res.ok) {
    const e = json as ApiErrorBody;
    return {
      ok: false,
      status: res.status,
      code: e.code ?? 'unknown',
      message: e.error ?? `Error HTTP ${res.status}.`,
    };
  }

  return { ok: true, data: unwrap ? unwrap(json) : (json as T) };
}

// ─── Briefings: CRUD ───────────────────────────────────────────────────

interface BriefingResponseBody {
  briefing: Briefing;
}
interface BriefingsListResponseBody {
  briefings: Briefing[];
}

/** Crea un briefing nuevo en el servidor. */
export async function createBriefing(
  payload: BriefingPayload,
): Promise<ApiResult<Briefing>> {
  return apiCall<Briefing>(
    'POST',
    '/api/briefings',
    payload,
    (j) => (j as BriefingResponseBody).briefing,
  );
}

/** Lista todos los briefings que el usuario actual puede ver. */
export async function listBriefings(): Promise<ApiResult<Briefing[]>> {
  return apiCall<Briefing[]>(
    'GET',
    '/api/briefings',
    undefined,
    (j) => (j as BriefingsListResponseBody).briefings,
  );
}

/** Detalle de un briefing por id. */
export async function getBriefing(id: string): Promise<ApiResult<Briefing>> {
  return apiCall<Briefing>(
    'GET',
    `/api/briefings/${encodeURIComponent(id)}`,
    undefined,
    (j) => (j as BriefingResponseBody).briefing,
  );
}

/** Actualiza un briefing existente (PATCH parcial). */
export async function updateBriefing(
  id: string,
  payload: BriefingPayload,
): Promise<ApiResult<Briefing>> {
  return apiCall<Briefing>(
    'PATCH',
    `/api/briefings/${encodeURIComponent(id)}`,
    payload,
    (j) => (j as BriefingResponseBody).briefing,
  );
}

/** Borra un briefing y todas sus fotos. */
export async function deleteBriefing(id: string): Promise<ApiResult<void>> {
  return apiCall<void>(
    'DELETE',
    `/api/briefings/${encodeURIComponent(id)}`,
  );
}

// ─── Briefings: fotos ──────────────────────────────────────────────────

/**
 * Sube una o varias fotos a un briefing existente. Devuelve el briefing
 * actualizado con la lista completa de fotos (incluidas las que ya
 * estaban). El caller debe haber comprobado el límite de 5 antes.
 */
export async function uploadBriefingPhotos(
  briefingId: string,
  files: File[],
): Promise<ApiResult<Briefing>> {
  const fd = new FormData();
  for (const f of files) {
    fd.append('photos', f, f.name);
  }
  return apiCall<Briefing>(
    'POST',
    `/api/briefings/${encodeURIComponent(briefingId)}/photos`,
    fd,
    (j) => (j as BriefingResponseBody).briefing,
  );
}

/** Borra una foto concreta de un briefing. */
export async function deleteBriefingPhoto(
  briefingId: string,
  photoId: string,
): Promise<ApiResult<void>> {
  return apiCall<void>(
    'DELETE',
    `/api/briefings/${encodeURIComponent(briefingId)}/photos/${encodeURIComponent(photoId)}`,
  );
}
